import { Injectable } from '@angular/core';
import { AdditionalField, FieldType } from 'src/app/shared/models/additional-field.model';
import { of, Observable } from 'rxjs';
import { EolctDataService } from 'src/app/shared/data-service/eolct-data.service';
import { CurrentOrderService } from 'src/app/shared/services/current-order.service';
import { StoreService } from 'projects/dop-core/src/public-api';
import { SettingsKey } from 'src/app/core/store/settings-config.keys';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { map } from 'rxjs/operators';
import { FormGroup, FormArray, AbstractControl, Validators } from '@angular/forms';
import { ISUNIQUE, ISMANDATORY, VALUE, CODE, AFS } from '../../features/guided-order/samples/constants/sample-constants';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { alphaNumeric } from '../validators/alphanumeric.validators';
import { HelperService } from './helper.service';
import { AdditionalFieldDto } from '../models/bulk-sample-creation/additional-field-dto.model';
import { OrderDataService } from '../data-service/order-data.service';

@Injectable({ providedIn: 'root' })
export class AdditionalFieldHelper {
  private enableTodayAsDefaultValueForDateTime = false;
  private sampleAdditionalFields: AdditionalField[];
  private orderAdditionalFields: AdditionalField[];

  get currentOrder() { return this.currentOrderService.currentOrder; }

  constructor(
    private orderDataService: OrderDataService,
    private currentOrderService: CurrentOrderService,
    private storeService: StoreService,
    private helperService: HelperService) {
  }

  getSampleAdditionalFields(): Observable<AdditionalField[]> {
    if (this.currentOrder.orderTemplate.code && this.currentOrder.SampleServiceCode) {
      return this.orderDataService.getSampleAdditionalFields(this.currentOrder.orderTemplate.code, this.currentOrder.SampleServiceCode, false)
        .pipe(map(x => {
          x.forEach(af => {
            af.name = !af.translatedName ? af.name : af.translatedName;
            af.value = this.setAdditionalFieldValue(af, this.enableTodayAsDefaultValueForDateTime);
            af.defaultValue = this.setDefaultValue(af);
          });
          return x;
        }));
    } else {
      return of([]);
    }
  }

  getOrderAdditionalFields(): Observable<AdditionalField[]> {
    if (this.currentOrder.orderTemplate.code) {
      return this.orderDataService.getOrderAdditionalFields(this.currentOrder.orderTemplate.code, false)
        .pipe(map(x => {
          x.forEach(af => {
            af.name = !af.translatedName ? af.name : af.translatedName;
            af.value = this.setAdditionalFieldValue(af, this.enableTodayAsDefaultValueForDateTime);
            af.defaultValue = this.setDefaultValue(af);
          });

          this.orderAdditionalFields = this.storeAdditionalFields(StoreKey.OrderAdditionalFields, x);
          return x;
        }));
    }
  }

  getOrderOptionalAfs() {
    return this.getAdditionalFields(true, false);
  }

  getOrderMandatoryAfs() {
    return this.getAdditionalFields(true, true);
  }

  getSampleOptionalAfs() {
    return this.getAdditionalFields(false, false);
  }

  getSampleMandatoryAfs() {
    return this.getAdditionalFields(false, true);
  }

  getAFName(af: FormGroup): string {
    let name = af.get('name').value;
    if (af.get(ISUNIQUE).value) {
      name = name + ' (unique)';
    }

    if (af.get(ISMANDATORY).value) {
      name = (name === null ? '' : name) + ' *';
    }
    return name;
  }

  setAfValue(af: FormGroup) {
    const type: FieldType = this.helperService.getAdditionalFieldType(af.value.fieldType);
    if (!(type === FieldType.BOOL || type === FieldType.DT || type === FieldType.DTO || type === FieldType.TMO)) {
      af.get(VALUE).setValue(af.get(VALUE).value.trim());
    }
  }

  storeAdditionalFields(storeKey: StoreKey, additionalFields: AdditionalField[]): AdditionalField[] {

    this.storeService.Get<any>('db_' + SettingsKey.enableTodayAsDefaultValueForDateTime).subscribe(y => {
      this.enableTodayAsDefaultValueForDateTime = y && (y === 'True' || y === 'true');
    });

    for (const af of additionalFields) {
      af.name = !af.translatedName ? af.name : af.translatedName;
      af.value = this.setAdditionalFieldValue(af, this.enableTodayAsDefaultValueForDateTime);
      af.defaultValue = this.setDefaultValue(af);
    }

    this.storeService.Save(storeKey, additionalFields, true);
    return additionalFields;
  }

  setAdditionalFieldValidators(af: AdditionalField): any[] {
    const result: any[] = [];

    if (af.isMandatory) {
      result.push(Validators.required);
    }

    const type: FieldType = this.helperService.getAdditionalFieldType(af.fieldType);
    switch (type) {
      case FieldType.TXT:
      case FieldType.MEMO:
        result.push(alphaNumeric());
        break;
      case FieldType.FLOAT:
      case FieldType.INT:
        result.push(RxwebValidators.numeric({
          allowDecimal: type === FieldType.FLOAT
        }));
        break;
      default:
        break;
    }

    return result;
  }

  formatFloatAf(af: FormGroup) {
    const isFloatAf = this.helperService.getAdditionalFieldType(af.get('fieldType').value) === FieldType.FLOAT;
    if (isFloatAf) {
      const parsedValue = Number(af.get(VALUE).value);
      if (isNaN(parsedValue) || af.get('value').value === "") {
        af.get(VALUE).setValue('');
      } else {
        af.get(VALUE).setValue(parsedValue.toString());
      }
    }
  }

  setDefaultValue(af: AdditionalField): any {
    const type: FieldType = this.helperService.getAdditionalFieldType(af.fieldType);
    if (type === FieldType.BOOL) {
      return this.setBooleanValue(af.defaultValue?.toString());
    } else if (type === FieldType.FLOAT) {
      return this.setFloatValue(af.defaultValue);
    } else if (type === FieldType.TMO) {
      return this.setTimeDefaultValue(af.defaultValue);
    }
    return af.defaultValue;
  }

  setFloatValue(defaultValue: string): string {
    const parsedValue = Number(defaultValue);
    if (isNaN(parsedValue) || defaultValue === '') {
      return '';
    } else {
      return parsedValue.toString();
    }
  }

  setBooleanValue(defaultValue: string): boolean {
    if (!defaultValue || defaultValue.toLowerCase() === 'false') {
      return false;
    }
    return true;
  }

  buildAfDtoList(adfs: AdditionalField[], isOrderFromExistingOrder?: boolean): AdditionalField[] {
    const afDtos: AdditionalFieldDto[] = [];
    for (const af of adfs) {
      const type: FieldType = this.helperService.getAdditionalFieldType(af.fieldType);
      if (isOrderFromExistingOrder && type === FieldType.BOOL) {
        afDtos.push(new AdditionalFieldDto(af.code, this.helperService.
          formatAdditionalFieldValue(af.code, af.value, af.fieldType)));
      }
      else {
        afDtos.push(new AdditionalFieldDto(af.code,
          this.helperService.formatAdditionalFieldValue(af.code,
            !(this.isDateType(af.fieldType) && this.enableTodayAsDefaultValueForDateTime)
              ? (this.isDateType(af.fieldType) ?
                (af.value ? this.setDefaultDate(af.value)?.toString()
                  : this.setDefaultDate(af.defaultValue)?.toString()) : (af.value && af.value.length > 0 ? af.value : af.defaultValue))
              : this.setDefaultDate(af.defaultValue)?.toString(), af.fieldType)));
      }
    }
    return afDtos;
  }

  buildAfDtoListWithUserInput(adfs: AdditionalField[]): AdditionalField[] {
    const afDtos: AdditionalFieldDto[] = [];
    for (const af of adfs) {
      afDtos.push(new AdditionalFieldDto(af.code,
        this.helperService.formatAdditionalFieldValue(af.code, af.value, af.fieldType)));
    }
    return afDtos;
  }

  getNewlyAddedAf(form: AbstractControl, adfs: AdditionalField[]): AdditionalField[] {
    const orderAfs = form.get(AFS) as FormArray;
    const newAFs: AdditionalField[] = [];
    adfs.forEach(af => {
      if (orderAfs.controls.findIndex(x => x.get(CODE).value === af.code) >= 0) {
        return;
      } else {
        newAFs.push({ ...af });
      }
    });
    return newAFs;
  }

  formatAfValue(af: AdditionalField): any {
    const type: FieldType = this.helperService.getAdditionalFieldType(af.fieldType);
    let value: any;
    switch (type) {
      case FieldType.DTO:
      case FieldType.DT:
        value = this.setDateValue(af.value);
        break;
      case FieldType.TMO:
        value = this.setTimeValue(af.value);
        break;
      case FieldType.BOOL:
        value = this.setBooleanValue(af.value?.toString());
        break;
      case FieldType.FLOAT:
        value = this.setFloatValue(af.value);
        break;

      default:
        value = af.value;
        break;
    }
    return value;
  }

  private isDateType(type: string): boolean {
    return (type === 'DT' || type === 'TMO' || type === 'DTO');
  }

  private setDefaultDate(value: string): Date {
    if (value) {
      return new Date(value);
    } else {
      if(this.enableTodayAsDefaultValueForDateTime)
        return new Date();
      else
        return null;
    }
  }

  private setAdditionalFieldValue(af: AdditionalField, isDefaultDateEnabled: boolean): any {
    const type: FieldType = this.helperService.getAdditionalFieldType(af.fieldType);
    let value: any;
    switch (type) {
      case FieldType.DTO:
      case FieldType.DT:
        value = this.setDateValue(af.defaultValue, isDefaultDateEnabled);
        break;
      case FieldType.TMO:
        value = this.setTimeValue(af.defaultValue, isDefaultDateEnabled);
        break;
      case FieldType.BOOL:
        value = this.setBooleanValue(af.defaultValue?.toString());
        break;
      case FieldType.FLOAT:
        value = this.setFloatValue(af.defaultValue);
        break;

      default:
        value = af.value ? af.value : af.defaultValue;
        break;
    }
    return value;
  }

  private getAdditionalFields(isOrder: boolean, isMandatory: boolean) {
    const additionalFields = (isOrder && this.orderAdditionalFields) ? this.orderAdditionalFields
      : (this.sampleAdditionalFields ? this.sampleAdditionalFields : []);

    return additionalFields.filter(x => x.isMandatory === isMandatory);
  }

  private setDateValue(defaultValue: any, isDefaultDateEnabled?: boolean): any {
    if (!defaultValue) {
      return isDefaultDateEnabled ? new Date() : null;
    }
    return new Date(defaultValue);
  }

  private setTimeValue(defaultValue: any, isDefaultDateEnabled?: boolean): any {
    if (!defaultValue) {
      return isDefaultDateEnabled ? new Date() : null;
    }
    return this.buildTimeValue(defaultValue);
  }

  private setTimeDefaultValue(defaultValue: any): any {
    if (!defaultValue) {
      return null;
    }
    return this.buildTimeValue(defaultValue);
  }

  private buildTimeValue(defaultValue: any): any {
    const date = new Date();
    if (isNaN(Date.parse(defaultValue))) {
      const splited = defaultValue.split(':');
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(),
        Number(splited[0]), Number(splited[1]));
    } else {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(),
        defaultValue.getHours(), defaultValue.getMinutes());
    }
  }
}
